import { Table, Text } from '@adc/parallax-component-library';

import type { PatientSearchVerifyResponseFound } from '~/api/types';
import { buildTestId } from '~/utils/buildTestId';

type PatientFoundTableProps = {
  selectedPatientId: string | undefined;
  selectFoundPatient: (patientId: string) => void;
  foundPatients: PatientSearchVerifyResponseFound[];
  testID: string;
};

const columns = [
  'lastName',
  'firstName',
  'dateOfBirth',
  'mrn',
  'email',
  'phone',
] as const;

const columnLabels = {
  lastName: 'Last Name',
  firstName: 'First Name',
  dateOfBirth: 'Date of Birth',
  mrn: 'Medical Record Number',
  email: 'Email',
  phone: 'Phone',
};

const formatColumn = (column: (typeof columns)[number], value: string) => {
  if (column === 'dateOfBirth') {
    return new Date(value).toLocaleDateString();
  }

  return value;
};

export const PatientFoundTable = ({
  selectFoundPatient,
  foundPatients,
  selectedPatientId,
  testID,
}: PatientFoundTableProps) => {
  return (
    <Table
      aria-label="Patient Found Table"
      height="unset"
      minHeight="unset"
      testID={testID}
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Select</Table.HeaderCell>

          {columns.map((column) => (
            <Table.HeaderCell
              key={column}
              testID={buildTestId({
                testID,
                suffix: ['header', column],
              })}
            >
              {columnLabels[column]}
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {foundPatients.map((foundPatient) => (
          <Table.Row
            key={foundPatient.id}
            testID={buildTestId({
              testID,
              suffix: ['row', foundPatient.id],
            })}
          >
            <Table.CheckboxCell
              testID={buildTestId({
                testID,
                suffix: ['checkbox', foundPatient.id],
              })}
              checked={selectedPatientId === foundPatient.id}
              onPress={() => {
                selectFoundPatient(foundPatient.id);
              }}
            />

            {columns.map((column) => (
              <Table.TextCell
                key={column}
                scope="col"
                testID={buildTestId({
                  testID,
                  suffix: ['cell', column, foundPatient.id],
                })}
              >
                {column === foundPatient.matchType ? (
                  <Text type="$bodyBase.medium">{foundPatient[column]}</Text>
                ) : (
                  formatColumn(column, foundPatient[column])
                )}
              </Table.TextCell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};
